import { useHistory } from 'react-router-dom';
import { Badge } from '@givebutter/primitives/badge';
import { Button } from '@givebutter/primitives/button';
import { faFileExport } from '@fortawesome/pro-regular-svg-icons';
import { faChartLineUp, faChartPieSimple, faShare } from '@fortawesome/pro-thin-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as GivebutterPlusSplash from 'components/givebutter-plus-splash/GivebutterPlusSplash';
import { NucleoIcon } from 'components/nucleo-icon';
import { HELP_CENTER_LINK } from 'constants/links';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import ReportingSplash from 'images/reporting-splash.webp';

const ReportingGivebutterPlusSplashPage = () => {
    const { accountId } = useCurrentAccount();
    const history = useHistory();

    return (
        <GivebutterPlusSplash.Root>
            <GivebutterPlusSplash.Content>
                <GivebutterPlusSplash.Header>
                    <GivebutterPlusSplash.Text>
                        <GivebutterPlusSplash.HeaderLogo />
                        <GivebutterPlusSplash.Title>Custom reports</GivebutterPlusSplash.Title>
                        <GivebutterPlusSplash.Description>
                            Create and automate customized reports to keep your team and
                            stakeholders informed, and enhance your fundraising strategies with
                            ease.
                        </GivebutterPlusSplash.Description>
                        <GivebutterPlusSplash.CallToAction />
                    </GivebutterPlusSplash.Text>
                    <GivebutterPlusSplash.HeaderImage>
                        <img src={ReportingSplash} alt="Reporting illustration" />
                    </GivebutterPlusSplash.HeaderImage>
                </GivebutterPlusSplash.Header>
                <GivebutterPlusSplash.FeatureCardRow>
                    <GivebutterPlusSplash.FeatureCard>
                        <GivebutterPlusSplash.FeatureCardIcon className="bg-gbp-gradient-1">
                            <FontAwesomeIcon icon={faChartPieSimple} size="xl" />
                        </GivebutterPlusSplash.FeatureCardIcon>
                        <GivebutterPlusSplash.FeatureCardText>
                            <GivebutterPlusSplash.FeatureCardTextHeading>
                                Calculate columns
                            </GivebutterPlusSplash.FeatureCardTextHeading>
                            <GivebutterPlusSplash.FeatureCardTextDescription>
                                Tailor reports to your specific needs, allowing for more detailed
                                and relevant insights that align with your organization’s goals.
                            </GivebutterPlusSplash.FeatureCardTextDescription>
                        </GivebutterPlusSplash.FeatureCardText>
                    </GivebutterPlusSplash.FeatureCard>

                    <GivebutterPlusSplash.FeatureCard>
                        <GivebutterPlusSplash.FeatureCardIcon className="bg-gbp-gradient-2">
                            <FontAwesomeIcon icon={faChartLineUp} size="xl" />
                        </GivebutterPlusSplash.FeatureCardIcon>
                        <GivebutterPlusSplash.FeatureCardText>
                            <GivebutterPlusSplash.FeatureCardTextHeading>
                                Track donor trends
                            </GivebutterPlusSplash.FeatureCardTextHeading>
                            <GivebutterPlusSplash.FeatureCardTextDescription>
                                Monitor metrics related to donors and transactions to understand
                                growth and retention patterns.
                            </GivebutterPlusSplash.FeatureCardTextDescription>
                        </GivebutterPlusSplash.FeatureCardText>
                    </GivebutterPlusSplash.FeatureCard>

                    <GivebutterPlusSplash.FeatureCard>
                        <GivebutterPlusSplash.FeatureCardIcon className="bg-gbp-gradient-3">
                            <FontAwesomeIcon icon={faShare} size="xl" />
                        </GivebutterPlusSplash.FeatureCardIcon>
                        <GivebutterPlusSplash.FeatureCardText>
                            <GivebutterPlusSplash.FeatureCardTextHeading>
                                Schedule reports
                            </GivebutterPlusSplash.FeatureCardTextHeading>
                            <GivebutterPlusSplash.FeatureCardTextDescription>
                                Share reports with employees, board members, and stakeholders to aid
                                in collective decision-making and transparency.
                            </GivebutterPlusSplash.FeatureCardTextDescription>
                        </GivebutterPlusSplash.FeatureCardText>
                    </GivebutterPlusSplash.FeatureCard>
                </GivebutterPlusSplash.FeatureCardRow>
                <div className="flex flex-col gap-8 pt-6">
                    <div className="flex flex-col gap-2">
                        <p className="text-2xl font-medium">Not ready to upgrade?</p>
                        <p className="sm:max-w-prose">
                            Every account comes equipped with access to export comprehensive contact
                            reports, personalized end-of-year giving summaries, detailed payout
                            analytics.
                        </p>
                    </div>
                    <div className="flex flex-col gap-4">
                        <GivebutterPlusSplash.FeatureCardRow>
                            <GivebutterPlusSplash.FeatureCard>
                                <div className="flex w-full items-center justify-between">
                                    <NucleoIcon icon="receipt" className="size-6 text-gray-600" />
                                    <Badge color="primary" size="lg">
                                        Free!
                                    </Badge>
                                </div>
                                <GivebutterPlusSplash.FeatureCardText>
                                    <GivebutterPlusSplash.FeatureCardTextHeading>
                                        Transactions
                                    </GivebutterPlusSplash.FeatureCardTextHeading>
                                    <GivebutterPlusSplash.FeatureCardTextDescription>
                                        Detailed breakdowns of transaction data for all campaigns or
                                        filter by a specific campaign.
                                    </GivebutterPlusSplash.FeatureCardTextDescription>
                                </GivebutterPlusSplash.FeatureCardText>
                                <Button
                                    variant="outline"
                                    color="neutral"
                                    className="self-start"
                                    onClick={() => {
                                        history.push(`/accounts/${accountId}/transactions`, {
                                            openExportDialog: true,
                                        });
                                    }}
                                >
                                    <FontAwesomeIcon icon={faFileExport} />
                                    Export
                                </Button>
                            </GivebutterPlusSplash.FeatureCard>
                            <GivebutterPlusSplash.FeatureCard>
                                <div className="flex w-full items-center justify-between">
                                    <NucleoIcon icon="users" className="size-6 text-gray-600" />
                                    <Badge color="primary" size="lg">
                                        Free!
                                    </Badge>
                                </div>
                                <GivebutterPlusSplash.FeatureCardText>
                                    <GivebutterPlusSplash.FeatureCardTextHeading>
                                        Giving Summaries
                                    </GivebutterPlusSplash.FeatureCardTextHeading>
                                    <GivebutterPlusSplash.FeatureCardTextDescription>
                                        End-of-year summaries, transaction data, and additional
                                        information for your contacts
                                    </GivebutterPlusSplash.FeatureCardTextDescription>
                                </GivebutterPlusSplash.FeatureCardText>
                                <Button
                                    variant="outline"
                                    color="neutral"
                                    className="self-start"
                                    onClick={() => {
                                        history.push(`/accounts/${accountId}/contacts`, {
                                            openExportSummariesDialog: true,
                                        });
                                    }}
                                >
                                    <FontAwesomeIcon icon={faFileExport} />
                                    Export
                                </Button>
                            </GivebutterPlusSplash.FeatureCard>
                            <GivebutterPlusSplash.FeatureCard>
                                <div className="flex w-full items-center justify-between">
                                    <NucleoIcon
                                        icon="circleDollar"
                                        className="size-6 text-gray-600"
                                    />
                                    <Badge color="primary" size="lg">
                                        Free!
                                    </Badge>
                                </div>
                                <GivebutterPlusSplash.FeatureCardText>
                                    <GivebutterPlusSplash.FeatureCardTextHeading>
                                        Payouts
                                    </GivebutterPlusSplash.FeatureCardTextHeading>
                                    <GivebutterPlusSplash.FeatureCardTextDescription>
                                        Export comprehensive payout records to reconcile
                                        transactions and with your bank account.
                                    </GivebutterPlusSplash.FeatureCardTextDescription>
                                </GivebutterPlusSplash.FeatureCardText>
                                <Button
                                    variant="outline"
                                    color="neutral"
                                    className="self-start"
                                    onClick={() => {
                                        history.push(`/accounts/${accountId}/payouts/history`, {
                                            openExportDialog: true,
                                        });
                                    }}
                                >
                                    <FontAwesomeIcon icon={faFileExport} />
                                    Export
                                </Button>
                            </GivebutterPlusSplash.FeatureCard>
                        </GivebutterPlusSplash.FeatureCardRow>
                        <GivebutterPlusSplash.FeatureCardRow>
                            <GivebutterPlusSplash.FeatureCard className="min-w-full">
                                <GivebutterPlusSplash.FeatureCardText>
                                    <GivebutterPlusSplash.FeatureCardTextHeading>
                                        Need help finding a report?
                                    </GivebutterPlusSplash.FeatureCardTextHeading>
                                    <GivebutterPlusSplash.FeatureCardTextDescription>
                                        Visit our Help Center for in-depth guides on exporting and
                                        importing your data.
                                    </GivebutterPlusSplash.FeatureCardTextDescription>
                                </GivebutterPlusSplash.FeatureCardText>
                                <Button
                                    variant="outline"
                                    color="neutral"
                                    className="self-start"
                                    onClick={() => window.open(HELP_CENTER_LINK, '_blank')}
                                >
                                    Help center &rarr;
                                </Button>
                            </GivebutterPlusSplash.FeatureCard>
                        </GivebutterPlusSplash.FeatureCardRow>
                    </div>
                </div>
                <GivebutterPlusSplash.Footer />
            </GivebutterPlusSplash.Content>
        </GivebutterPlusSplash.Root>
    );
};

export { ReportingGivebutterPlusSplashPage };
